import { GoogleLogin } from '@react-oauth/google';
import { getClientIdMicrosoft } from 'app/config/config';
import { defaultConfig } from 'app/config/environment';
import { recoverAccount } from 'core/crud/auth.crud';
import useScopes from 'core/hooks/useScopes';
import TextHtml from 'core/modules/atoms/TextHtml';
import ForgotPasswordForm from 'core/modules/components/forms/ForgotPasswordForm/ForgotPasswordForm';
import { successResponse } from 'core/utils/utils';
import i18next from 'i18next';
import * as Msal from 'msal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { AuthProviderButton, Button, Dialog, FormField, InputCheckbox, InputPassword, InputText, Loading } from 'ui';
import initLayoutConfig from '../../../../app/config/layout/LayoutConfig';
import LineWithText from '../../atoms/LineWithText';
import LoginOraculo from '../ssoOraculo/LoginOraculo';

const POLITICS = { LEGAL: 'legal', PRIVACY: 'privacy' };
const FORGOT_PASSWORD_STATE = { INITIAL: 'initial', LOADING: 'loading', SUCCESS: 'success' };

const LoginForm = (props) => {
  const { addToast } = useToasts();
  const { systemRequirements } = useScopes();
  const { register, handleSubmit, errors } = useForm();
  const { t, legal, privacy, onSubmit, onSSO, loading, showRememberMe, showForgotPassword } = props;

  const [showLegal, setShowLegal] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showSystem, setShowSystem] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [forgotPasswordState, setForgotPasswordState] = useState(FORGOT_PASSWORD_STATE.INITIAL);
  const [buttonWidth, setButtonWidth] = useState('300px'); // Ancho predeterminado
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleWindowResize = () => {
      if (buttonRef.current !== null) {
        const width = buttonRef.current.offsetWidth;
        setButtonWidth(`${width}px`);
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  useEffect(() => {
    if (buttonRef.current) {
      const width = buttonRef.current.offsetWidth;
      setButtonWidth(`${width}px`);
    }
  }, []);

  const onLogin = (data) => {
    onSubmit && onSubmit(data.email, data.password);
  };

  const onLoginSSO = (token) => {
    onSSO && onSSO(token, 'google');
  };

  const onLoginMicrosoft = (onSubmitSSO, onErrorSSO) => {
    const clientId = getClientIdMicrosoft(window.location.origin);
    const config = { auth: { clientId, redirectUri: window.location.origin } };

    let myMSALObj = new Msal.UserAgentApplication(config);
    let requestObj = { scopes: ['user.read'], prompt: 'select_account' };

    myMSALObj
      .loginPopup(requestObj)
      .then(async function () {
        myMSALObj
          .acquireTokenSilent(requestObj)
          .then(async (response) => {
            const user = {
              name: response.account.name,
              email: response.account.userName,
              sso_token: response.accessToken,
              identity_provider: 'microsoft',
            };
            //onSubmitSSO(user);
            onSSO && onSSO(user.sso_token, user.identity_provider);
          })
          .catch(function (error) {
            console.error('SSO ms error: ', error);
            myMSALObj
              .acquireTokenPopup(requestObj)
              .then(async function (response) {
                const user = {
                  name: response.account.name,
                  email: response.account.userName,
                  sso_token: response.accessToken,
                  identity_provider: 'microsoft',
                };
                //onSubmitSSO(user);
                onSSO && onSSO(user.sso_token, user.identity_provider);
              })
              .catch(function (error) {
                onErrorSSO && onErrorSSO(error);
              });
          });
      })
      .catch(function (error) {
        onErrorSSO && onErrorSSO(error);
      });
  };

  const closeShowForgotPasswordDialog = () => {
    setShowForgotPasswordDialog(false);
    setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
  };

  async function onSendChangePassword(email) {
    setForgotPasswordState(FORGOT_PASSWORD_STATE.LOADING);
    const response = await recoverAccount(email, { link: defaultConfig.BASE_URL_ + 'recover-password?token=[TOKEN]&lang=[LANG]' });
    if (successResponse(response)) {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.SUCCESS);
    } else {
      setForgotPasswordState(FORGOT_PASSWORD_STATE.INITIAL);
      addToast(t(`login:USER_NOT_FOUND`), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }

  function onShowPolitics(selection) {
    const politics = selection === POLITICS.PRIVACY ? privacy : legal;
    if (politics?.endsWith('pdf')) {
      if (selection === POLITICS.PRIVACY) window.open(defaultConfig.BASE_URL_ + 'privacy', '_blank');
      if (selection === POLITICS.LEGAL) window.open(defaultConfig.BASE_URL_ + 'terms', '_blank');
    } else {
      if (selection === POLITICS.PRIVACY) setShowPrivacy(selection);
      if (selection === POLITICS.LEGAL) setShowLegal(selection);
    }
  }

  return (
    <div className="login-form">
      {forgotPasswordState === FORGOT_PASSWORD_STATE.LOADING && <Loading />}

      <div className="login-form__form-container">
        <form onSubmit={handleSubmit(onLogin)} className="login-form__form">
          <div className="login-form__item">
            <h1 className="login-form__title">{t('login:Log in')}</h1>
          </div>
          <div className="login-form__item">
            <FormField error={!!errors.email} errorMessage={t('auth:Enter your username')} label={t('auth:User')}>
              <InputText icon="user" iconLeft name="email" placeholder={t('auth:Username')} referer={register({ required: true })} />
            </FormField>
          </div>
          <div className="login-form__item">
            <FormField error={!!errors.password} errorMessage={t('auth:Enter your password')} label={t('auth:Password')}>
              <InputPassword iconLeft name="password" placeholder={t('auth:Password')} referer={register({ required: true })} />
            </FormField>
          </div>
          <div className="login-form__item login-form__item--subactions">
            {showRememberMe && (
              <div className="login-form__checkbox-container">
                <InputCheckbox label={t('Remember me')} />
              </div>
            )}
            {showForgotPassword && (
              <button
                type="button"
                className="button-accessibility-only login-form__text login-form__text--forget"
                onClick={() => setShowForgotPasswordDialog(true)}
                aria-label={t('buttons:Forgot your password')}
                aria-haspopup="dialog"
                aria-controls="dialog-forgot-password"
              >
                {t('Forgot your password?')}
              </button>
            )}
          </div>
          <div className="login-form__item">
            <Button testid="btn-login" text={t('Log in')} isLoading={loading} type="submit" isFullWidth />
          </div>
          {
            <>
              <div className="login-form__item">
                <LineWithText text={t('Or')} />
              </div>
              <div className="login-form__third-parties">
                <div className="login-form__item login-form__item--google">
                  <GoogleLogin
                    width={buttonWidth}
                    text="signin_with"
                    onSuccess={(credentialResponse) => {
                      onLoginSSO(credentialResponse.credential);
                    }}
                    onError={(response) => {
                      console.log('google fail ', response);
                    }}
                    locale={i18next.language}
                  />
                </div>
                <div className="login-form__item" ref={buttonRef}>
                  <AuthProviderButton testid="btn-sso-microsoft" provider="microsoft" text={t('login:Login with Microsoft')} onClick={onLoginMicrosoft} />
                </div>

                {initLayoutConfig.login.showOraculo && (
                  <div className="login-form__item">
                    <LoginOraculo text={t('login:Login with Polp')} />
                  </div>
                )}
              </div>
            </>
          }
          <Link to="/auth/signup" className="button-accessibility-only login-form__signup-button">
            <span className="login-form__text">
              {t('If you don’t have an account yet,')}
              <span className="login-form__link"> {t('register here')}</span>
            </span>
          </Link>

          <div className="login-form__politics">
            {legal && (
              <button
                type="button"
                className="button-accessibility-only login-form__legal-advice"
                onClick={() => onShowPolitics(POLITICS.LEGAL)}
                aria-label={t('buttons:View legal advice')}
                aria-haspopup="dialog"
                aria-controls="dialog-legal-advice"
              >
                {t('login:Legal advice')}
              </button>
            )}
            {privacy && (
              <button
                type="button"
                className="button-accessibility-only login-form__privacy"
                onClick={() => onShowPolitics(POLITICS.PRIVACY)}
                aria-label={t('buttons:View privacy policy')}
                aria-haspopup="dialog"
                aria-controls="dialog-privacy"
              >
                {t('login:Privacy')}
              </button>
            )}
            <button
              type="button"
              className="button-accessibility-only login-form__system-requirements"
              onClick={() => setShowSystem(true)}
              aria-label={t('buttons:View system requirements')}
              aria-haspopup="dialog"
              aria-controls="dialog-system-requirements"
            >
              {t('login:System Requirements')}
            </button>
          </div>
        </form>
      </div>

      <Dialog
        id="dialog-legal-advice"
        size="xl"
        className="dialog-terms"
        title={t('login:Legal advice').toUpperCase()}
        isOpen={showLegal}
        onClose={() => setShowLegal(false)}
      >
        <TextHtml text={legal} />
      </Dialog>

      <Dialog
        id="dialog-privacy"
        size="xl"
        className="dialog-terms"
        title={t('login:Privacy').toUpperCase()}
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      >
        <TextHtml text={privacy} />
      </Dialog>

      <Dialog
        id="dialog-system-requirements"
        size="xl"
        className="dialog-terms"
        title={t('login:System Requirements').toUpperCase()}
        isOpen={showSystem}
        onClose={() => setShowSystem(false)}
      >
        <TextHtml text={systemRequirements} />
      </Dialog>

      <Dialog
        id="dialog-forgot-password"
        size="s"
        className="dialog-forgot-password"
        title={t('login:Recover password')}
        isOpen={showForgotPasswordDialog}
        onClose={closeShowForgotPasswordDialog}
      >
        <ForgotPasswordForm t={t} state={forgotPasswordState} onCancel={closeShowForgotPasswordDialog} onSuccess={onSendChangePassword} />
      </Dialog>
    </div>
  );
};

LoginForm.propTypes = {
  legal: PropTypes.string,
  loading: PropTypes.bool,
  onSSO: PropTypes.func,
  onSubmit: PropTypes.func,
  privacy: PropTypes.string,
  showForgotPassword: PropTypes.bool,
  showRememberMe: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default withTranslation(['login', 'auth', 'common', 'recover'])(LoginForm);
